// priceItems (DESIGN.md 4.2, TOOLS.md 4): the only source of numbers in a
// Quote Brain run (tool T12, never-do rule N1). It turns each item's build-up
// into an amount with the business's pricing methodology, which the caller
// injects from pricing-core (backend/src/shared/pricing-core/, slice PC):
// the method on the business's Pricing page (rate-book-core.js
// pricingPageMethodology), or rjBirdMethodology in the offline harness. This
// adapter adds no arithmetic of its own beyond summing build-up lines and the
// builder-notes lock; band, admin fee, GST, rounding, minimum attendance and
// the statutory rows are the methodology's, and the skill's total rule is
// pricing-core's (clean-subtotal.js).
//
// A price the builder states in the notes is the price printed: a notes item
// is the exact sum of its notes figures, GST-inclusive. No rounding and no
// clean-subtotal nudge moves it. Only engine-priced items move. On RJ BIRD's
// first live brain job (2026-10-28) the nudge took "$1440 incl GST" to $1430,
// and the builder had to change it back.

import { findNotesLine, notesTotalLines, notesTotalLeavesOutHbcf } from './notes.js';
import { cleanTotalDeltas, isCleanTotal, spreadTotalNudge } from '../pricing-core/clean-subtotal.js';

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
// The methodology's whole statutory row, kept for the project write: the
// Stage 5 statutory check reads its authority fields (amountLocked,
// clientScopeItem, complianceStatus), so a partial copy failed it. Tool
// results to the model name only the id, title and amount (handlers.js).
const managedRowOf = (row) => JSON.parse(JSON.stringify(row));
const CLIENT_KINDS = new Set(['work', 'prelims', 'investigation']);
const STATUTORY_TITLE_RE = /\b(?:preliminar(?:y|ies)|hbcf|home\s+building\s+compensation|dbpa|regulated\s+design|srd\b|fair\s+trading)\b/i;
const LABOURER_RE = /labourer/i;

export function createPriceItems({ methodology: M, rateBook }) {
    const required = ['resolveRjBirdPricingMethodology', 'priceAuthoritativeRateCardStreams', 'reconcileFrancisComplianceScope', 'reconcileFrancisHbcfScope', 'isManagedComplianceRow', 'isManagedHbcfRow'];
    const missing = required.filter((k) => typeof M?.[k] !== 'function');
    if (missing.length) throw new Error(`pricing methodology is missing ${missing.join(', ')}`);
    const resolved = M.resolveRjBirdPricingMethodology(rateBook.methodologyLayer || null);

    function lineReceipt(line) {
        if (line.source === 'rate') {
            const r = rateBook.get(line.rateId);
            return { ...line, description: r.description, unit: r.unit, sellExGst: r.sellExGst, includesLabour: r.includesLabour, exGst: round2(r.sellExGst * line.qty) };
        }
        if (line.source === 'labour') {
            const r = rateBook.dayRate(line.trade);
            // dayRateId: the Pricing page row the day rate came from, so a
            // "which quotes use this price" view counts labour lines too.
            return { ...line, perDayExGst: r.perDayExGst, basis: r.basis, ...(r.rateId ? { dayRateId: r.rateId } : {}), exGst: round2(r.perDayExGst * line.crew * line.days) };
        }
        if (line.source === 'notes') {
            const exGst = line.basis === 'inc' ? round2(line.figure / 1.1) : line.figure;
            return { ...line, exGst, incGst: line.basis === 'inc' ? line.figure : round2(line.figure * 1.1) };
        }
        if (line.source === 'market') {
            // A cited supplier price whose 30% uplift the shared evidence rules
            // already applied (market-price/evidence.js). Priced like a rate line.
            return { ...line, exGst: round2(Number(line.sellExGst) * line.qty) };
        }
        return { ...line, exGst: 0, unpriced: true };
    }

    // foldStatutory (B2 piece 2): code-added HBCF rows read as one
    // Preliminaries item, not as fragments (B1: Westmead and Naremburn).
    // Amounts are the methodology's; only how the rows are grouped changes.
    // cleanSubtotal (B3 piece 'round', fault N6): the skill's total rule, so
    // the ex-GST subtotal lands on a whole $100 where no engine-priced item
    // moves more than about 1%. A builder-notes item never moves: RJ Bird's
    // sent quotes print the notes' price even when the subtotal is not a
    // whole $100 (five C2 jobs, and Unit 17 on 2026-10-28).
    // foldIntoSingleItem (B5 piece 'fit', B4.md T4): a builder-notes job
    // written as one lump-sum item, as RJ Bird prints it, takes the HBCF
    // premium into that item when there is no prelims item to hold it.
    // hbcfFollowsNotes (B6 piece 'detail', B5.md U7): a job priced wholly from
    // builder's notes whose total leaves HBCF out gets no premium from code,
    // whatever compliance.hbcf says (Bellevue run 2 added $2,240).
    // fixedStatutoryIncGst: after the first run the statutory rows are not
    // priced again (statutoryFor returns none), but their stored amounts are
    // still in the quote's total. A Stage 4 reprice passes them, so the clean
    // subtotal aims at the total the first run aimed at (F2 money review,
    // 2026-10-29: without them a reprice moved untouched items on HBCF jobs).
    return function priceItems({ items = [], quoteFields = {}, notesText = '', firstRun = true, foldStatutory = false, cleanSubtotal = false, foldIntoSingleItem = false, hbcfFollowsNotes = false, fixedStatutoryIncGst = 0 } = {}) {
        const flags = [];
        const flag = (code, message, extra = {}) => flags.push({ code, message, blocking: false, ...extra });

        let band = 'medium';
        if (quoteFields.band?.value && quoteFields.band.value !== 'medium') {
            if (quoteFields.band.notesLine && findNotesLine(notesText, quoteFields.band.notesLine)) band = quoteFields.band.value;
            else flag('band-not-from-notes', 'The answer sheet fixes the band at medium. Another band needs a line from the builder notes; medium was used.');
        }

        const prepared = items.map((item) => {
            const lines = item.buildUp.map(lineReceipt);
            const engineExGst = round2(lines.filter((l) => l.source === 'rate' || l.source === 'labour' || l.source === 'market').reduce((s, l) => s + l.exGst, 0));
            const notesLines = lines.filter((l) => l.source === 'notes');
            for (const l of lines.filter((x) => x.unpriced)) flag('price-to-confirm', `"${l.what}" has no rate-book or notes price. The item is priced without it.`, { itemId: item.id });
            if (lines.some((l) => l.source === 'rate' && l.includesLabour) && lines.some((l) => l.source === 'labour' && !LABOURER_RE.test(l.trade))) {
                flag('double-labour', 'A supply-and-install rate line already includes its labour, and a trade day rate is added on top (pricing-bands.md, the double-labour trap).', { itemId: item.id });
            }
            return { item, lines, engineExGst, notesLines };
        });

        const priceStreams = (entries) => (entries.length ? M.priceAuthoritativeRateCardStreams(entries, { methodology: resolved, band }) : { items: [] });
        const out = new Map();

        // Engine-priced client items: one stream per item, grouped by approval group.
        const engine = prepared.filter((p) => CLIENT_KINDS.has(p.item.kind) && p.notesLines.length === 0 && p.engineExGst > 0);
        const priced = priceStreams(engine.map((p) => ({ id: p.item.id, administrationGroupId: p.item.approvalGroup || 'project', finalExGst: p.engineExGst })));
        for (const r of priced.items) out.set(r.id, { stream: r, priceSource: 'engine' });

        // Optional and alternative items: each priced alone, outside the total.
        for (const p of prepared.filter((x) => !CLIENT_KINDS.has(x.item.kind) && x.notesLines.length === 0 && x.engineExGst > 0)) {
            const r = priceStreams([{ id: p.item.id, administrationGroupId: p.item.id, finalExGst: p.engineExGst }]).items[0];
            out.set(p.item.id, { stream: r, priceSource: 'engine' });
        }

        // Builder-notes items: locked at exactly the sum of their notes
        // figures, unrounded, so a stated price prints as stated. The ex-GST
        // figures are summed before GST, so their subtotal is the stated sum.
        for (const p of prepared.filter((x) => x.notesLines.length > 0)) {
            const figures = (inc) => p.notesLines.filter((l) => (l.basis === 'inc') === inc).reduce((s, l) => s + Number(l.figure), 0);
            const incGst = round2(figures(true) + figures(false) * 1.1);
            const amountIncGst = incGst;
            let engineEstimateIncGst = null;
            if (p.engineExGst > 0) {
                engineEstimateIncGst = priceStreams([{ id: p.item.id, administrationGroupId: p.item.id, finalExGst: p.engineExGst }]).items[0].amountIncGst;
                if (Math.abs(amountIncGst - engineEstimateIncGst) / engineEstimateIncGst > 0.2) {
                    flag('notes-vs-engine-gap', `The notes price (${amountIncGst} inc GST) is ${Math.round((amountIncGst / engineEstimateIncGst - 1) * 100)}% from this item's own build-up (${engineEstimateIncGst} inc GST).`, { itemId: p.item.id });
                }
            } else {
                flag('notes-no-cross-check', 'This notes-priced item has no rate or labour lines to check the noted price against.', { itemId: p.item.id });
            }
            out.set(p.item.id, { locked: { incGst, amountIncGst, engineEstimateIncGst }, priceSource: 'builder-notes' });
        }

        const itemResults = prepared.map((p) => {
            const o = out.get(p.item.id);
            const s = o?.stream;
            const amountIncGst = o?.locked ? o.locked.amountIncGst : (s ? s.amountIncGst : 0);
            return {
                itemId: p.item.id,
                kind: p.item.kind,
                amountIncGst,
                priceSource: o ? o.priceSource : 'unpriced',
                buildUpExGst: p.engineExGst,
                notesExGst: round2(p.notesLines.reduce((sum, l) => sum + l.exGst, 0)),
                ...(o?.locked ? { engineEstimateIncGst: o.locked.engineEstimateIncGst } : {}),
                lines: p.lines,
                ...(s ? {
                    approvalGroup: s.administrationGroupId,
                    bandedBuildUpExGst: s.bandedBaseExGst,
                    adminShareExGst: s.bandedAdministrationShareExGst,
                    unroundedIncGst: s.unroundedIncGst,
                    minimumAttendanceIncGst: s.minimumAttendanceAllocationIncGst || 0,
                } : {}),
            };
        });

        // Statutory rows (TOOLS.md 4.4), reconciled on the first run only.
        // A function of the item amounts, so the clean-subtotal rule can try
        // a nudge and recompute the rows that depend on the items.
        const compliance = { regulated: 'no', statutory: 'engine', hbcf: 'engine', ...(quoteFields.compliance || {}) };
        const clientItemsAll = items.filter((i) => CLIENT_KINDS.has(i.kind));
        const notesLeaveOutHbcf = hbcfFollowsNotes && clientItemsAll.length > 0
            && clientItemsAll.every((i) => i.buildUp.some((l) => l.source === 'notes')) && notesTotalLeavesOutHbcf(notesText);
        const hbcfWaived = compliance.hbcf !== 'engine' || notesLeaveOutHbcf;
        function statutoryFor(results) {
            const statFlags = [];
            const clientRows = results
                .filter((r) => CLIENT_KINDS.has(r.kind))
                .map((r) => {
                    const item = items.find((i) => i.id === r.itemId);
                    return { id: r.itemId, code: r.itemId, title: item.title, amount: String(r.amountIncGst) };
                });
            let statutory = [];
            if (firstRun) {
                if (compliance.statutory === 'engine') {
                    const collisions = clientRows.filter((r) => STATUTORY_TITLE_RE.test(r.title));
                    if (collisions.length) {
                        statFlags.push({ code: 'statutory-collision', blocking: true, message: `Items ${collisions.map((c) => c.id).join(', ')} name a statutory component while compliance.statutory is "engine". Code adds those rows: remove the item, or set statutory to "builder-notes" and price it from the notes.` });
                    } else {
                        const r = compliance.regulated === 'yes' || compliance.regulated === 'provisional'
                            ? M.reconcileFrancisComplianceScope(clientRows, {
                                methodology: resolved,
                                compliancePosture: { status: compliance.regulated === 'yes' ? 'confirmed_required' : 'provisional_required', reason: compliance.reason || null },
                                hbcfWaived,
                            })
                            : M.reconcileFrancisHbcfScope(clientRows, { methodology: resolved, hbcfWaived });
                        statutory = r.scopeItems.filter((row) => M.isManagedComplianceRow(row) || M.isManagedHbcfRow(row));
                        if (compliance.regulated === 'provisional') {
                            statFlags.push({ code: 'compliance-provisional', blocking: true, message: 'Regulated-work status is provisional: no SRD or DBPA is priced, and issue is held until it is confirmed.' });
                        }
                    }
                } else if (compliance.hbcf === 'engine' && !notesLeaveOutHbcf) {
                    const r = M.reconcileFrancisHbcfScope(clientRows, { methodology: resolved, hbcfWaived: false });
                    if (r.status === 'collision') {
                        statFlags.push({ code: 'statutory-collision', blocking: true, message: 'An item title names HBCF while hbcf is "engine". Rename it, or set hbcf to "builder-notes".' });
                    } else {
                        // The builder's notes price the application and contract; code adds the premium only.
                        statutory = r.scopeItems.filter((row) => M.isManagedHbcfRow(row) && row.statutoryKind === 'hbcf_premium_estimate');
                    }
                }
            }
            const rows = statutory.map((row) => ({
                id: row.id,
                title: row.title,
                summary: row.description,
                lines: (row.inclusions || []).filter(Boolean),
                amountIncGst: Number(row.amount),
                placement: M.isManagedComplianceRow(row) ? 'first' : 'last',
                components: row.costBreakdown?.complianceComponents || row.costBreakdown?.otherCosts || [],
                // The methodology's own row, so the project write keeps the id and
                // marker the statutory step recognises (Add or Remove HBCF later).
                row: managedRowOf(row),
            }));
            return { rows, flags: statFlags };
        }

        let chosen = { results: itemResults, ...statutoryFor(itemResults) };
        const fixedStatutory = firstRun ? 0 : Math.max(0, Number(fixedStatutoryIncGst) || 0);
        const totalOf = (c) => round2(c.results.filter((r) => CLIENT_KINDS.has(r.kind)).reduce((sum, r) => sum + r.amountIncGst, 0) + c.rows.reduce((sum, r) => sum + r.amountIncGst, 0) + fixedStatutory);
        if (cleanSubtotal && totalOf(chosen) > 0 && !isCleanTotal(totalOf(chosen))) {
            // Engine-priced work items move, never a builder-notes item, a
            // Preliminaries item or a statutory row; an item held at the
            // minimum attendance never moves down.
            const movable = itemResults
                .filter((r) => CLIENT_KINDS.has(r.kind) && r.kind !== 'prelims' && r.amountIncGst > 0 && r.priceSource === 'engine')
                .map((r) => ({ id: r.itemId, amountIncGst: r.amountIncGst, ...(r.minimumAttendanceIncGst ? { floorIncGst: r.amountIncGst } : {}) }));
            for (const delta of cleanTotalDeltas()) {
                const moved = spreadTotalNudge(movable, delta);
                if (!moved) continue;
                const results = itemResults.map((r) => (moved.has(r.itemId)
                    ? { ...r, amountIncGst: moved.get(r.itemId), cleanSubtotalNudgeIncGst: round2(moved.get(r.itemId) - r.amountIncGst) }
                    : r));
                const trial = { results, ...statutoryFor(results) };
                // A nudge never adds or drops a statutory row (the HBCF threshold).
                const sameRows = trial.rows.map((r) => r.id).join('|') === chosen.rows.map((r) => r.id).join('|');
                if (sameRows && isCleanTotal(totalOf(trial))) { chosen = trial; break; }
            }
        }
        itemResults.splice(0, itemResults.length, ...chosen.results);
        flags.push(...chosen.flags);
        // Said only where HBCF would otherwise apply: works over $20,000 inc GST.
        const worksIncGst = round2(itemResults.filter((r) => CLIENT_KINDS.has(r.kind) && r.kind !== 'prelims').reduce((s, r) => s + r.amountIncGst, 0));
        if (notesLeaveOutHbcf && compliance.hbcf === 'engine' && worksIncGst > 20000) {
            flag('hbcf-not-in-notes-total', 'The builder\'s notes total leaves HBCF out, so code priced no HBCF premium (rule 21). Set hbcf to "not-required" with that reason, list no HBCF certificate, and raise_confirm whether the job needs HBCF.');
        }
        let statutoryRows = chosen.rows;

        if (foldStatutory && statutoryRows.length) {
            const hbcfRows = statutoryRows.filter((r) => /hbcf/i.test(r.id));
            const clientItems = items.filter((i) => CLIENT_KINDS.has(i.kind));
            const lumpSum = foldIntoSingleItem && clientItems.length === 1 && clientItems[0].buildUp.some((l) => l.source === 'notes') ? clientItems[0] : null;
            const host = items.find((i) => i.kind === 'prelims') || lumpSum;
            if (compliance.statutory === 'engine' && compliance.regulated === 'no' && hbcfRows.length > 1) {
                // Not regulated, over the threshold: the application and the
                // premium print as one Preliminaries & Administration item.
                const merged = {
                    id: 'francis-hbcf-preliminaries',
                    title: 'Preliminaries & Administration',
                    summary: 'Home Building Compensation Fund application and insurance for the residential building works included in this quote.',
                    lines: ['Prepare, lodge and administer the HBCF application', 'Supply HBCF Insurance Certificate'],
                    amountIncGst: round2(hbcfRows.reduce((s, r) => s + r.amountIncGst, 0)),
                    placement: 'first',
                    components: hbcfRows.flatMap((r) => r.components),
                    merged: hbcfRows.map((r) => r.id),
                    row: null,
                    mergedRows: hbcfRows.map((r) => r.row),
                };
                statutoryRows = [merged, ...statutoryRows.filter((r) => !hbcfRows.includes(r))];
            } else if (compliance.statutory === 'builder-notes' && host) {
                // The builder's notes price the preliminaries: the premium code
                // computes is part of that item's amount, not an item of its own.
                statutoryRows = statutoryRows.map((r) => ({ ...r, foldedInto: host.id }));
                const hostResult = itemResults.find((r) => r.itemId === host.id);
                hostResult.statutoryIncGst = round2(statutoryRows.reduce((s, r) => s + r.amountIncGst, 0));
                hostResult.printedAmountIncGst = round2(hostResult.amountIncGst + hostResult.statutoryIncGst);
            }
        }

        const clientTotal = round2(itemResults.filter((r) => CLIENT_KINDS.has(r.kind)).reduce((s, r) => s + r.amountIncGst, 0)
            + statutoryRows.reduce((s, r) => s + r.amountIncGst, 0));
        const subtotalExGst = round2(clientTotal / 1.1);
        const totals = {
            totalIncGst: clientTotal,
            subtotalExGst,
            gst: round2(clientTotal - subtotalExGst),
            optionalIncGst: round2(itemResults.filter((r) => !CLIENT_KINDS.has(r.kind)).reduce((s, r) => s + r.amountIncGst, 0)),
        };

        const totalLines = notesTotalLines(notesText);
        if (totalLines.length && clientTotal > 0) {
            const gaps = totalLines.map((t) => {
                const incGst = t.basis === 'inc' ? t.value : t.value * 1.1;
                return { ...t, incGst, gap: clientTotal / incGst - 1 };
            });
            const nearest = gaps.sort((a, b) => Math.abs(a.gap) - Math.abs(b.gap))[0];
            if (Math.abs(nearest.gap) > 0.02) {
                flag('notes-total-gap', `The quote total (${clientTotal} inc GST) is ${Math.round(nearest.gap * 100)}% from the notes line "${nearest.line}" (read as ${nearest.basis === 'inc' ? 'inc' : 'ex'} GST).${foldStatutory ? ' If the difference is work the notes describe but leave out of their total, keep that work in the main scope and raise_confirm the difference.' : ''}`);
            }
        }

        // The method and rate book these amounts came from, so Adopt and a
        // Stage 4 reprice keep the run's own method after the Pricing page
        // changes, and a run re-pinned to a changed page prices again.
        return { ok: true, band, items: itemResults, statutoryRows, totals, flags, methodology: resolved, rateBookHash: rateBook.hash ?? null };
    };
}
